import { UpdateResolver } from '@urql/exchange-graphcache';
import {
  CreateCompanyDatabaseColumnMutation,
  CreateCompanyDatabaseColumnMutationVariables,
  CreateCompanyDatabaseMutation,
  CreateCompanyDatabaseMutationVariables,
  DeleteCompanyDatabaseColumnMutation,
  DeleteCompanyDatabaseColumnMutationVariables,
  DeleteCompanyDatabaseMutation,
  DeleteCompanyDatabaseMutationVariables,
  ImportCompanyDatabaseDataMutation,
  ImportCompanyDatabaseDataMutationVariables,
  ListCompanyDatabasesDocument,
  UpdateCompanyDatabaseColumnMutation,
  UpdateCompanyDatabaseColumnMutationVariables,
  UpdateCompanyDatabaseMutation,
  UpdateCompanyDatabaseMutationVariables,
} from '../../gql/graphql';
import { clearDatabaseLookupCacheForDatabase } from '../../hooks/useDatabaseLookup';

export const createCompanyDatabaseResolver: UpdateResolver<
  CreateCompanyDatabaseMutation,
  CreateCompanyDatabaseMutationVariables
> = (result, _args, cache) => {
  const newDatabase = result.createCompanyDatabasis?.companyDatabasis;
  if (!newDatabase) return;

  cache.updateQuery({ query: ListCompanyDatabasesDocument }, (data) => {
    if (data?.companyDatabases) {
      data.companyDatabases.push(newDatabase);
      // Sort by name ascending to match the query order
      data.companyDatabases.sort((a, b) => a.name.localeCompare(b.name));
    }
    return data;
  });
};

export const updateCompanyDatabaseResolver: UpdateResolver<
  UpdateCompanyDatabaseMutation,
  UpdateCompanyDatabaseMutationVariables
> = (result, _args, cache) => {
  const updatedDatabase = result.updateCompanyDatabasis?.companyDatabasis;
  if (!updatedDatabase) return;

  cache.updateQuery({ query: ListCompanyDatabasesDocument }, (data) => {
    if (data?.companyDatabases) {
      const index = data.companyDatabases.findIndex(
        (db) => db.id === updatedDatabase.id
      );
      if (index !== -1) {
        data.companyDatabases[index] = updatedDatabase;
        // Re-sort by name ascending in case name changed
        data.companyDatabases.sort((a, b) => a.name.localeCompare(b.name));
      }
    }
    return data;
  });
};

export const deleteCompanyDatabaseResolver: UpdateResolver<
  DeleteCompanyDatabaseMutation,
  DeleteCompanyDatabaseMutationVariables
> = (result, _args, cache) => {
  const deletedDatabase = result.deleteCompanyDatabasis?.companyDatabasis;
  if (!deletedDatabase) return;

  cache.updateQuery({ query: ListCompanyDatabasesDocument }, (data) => {
    if (data?.companyDatabases) {
      data.companyDatabases = data.companyDatabases.filter(
        (db) => db.id !== deletedDatabase.id
      );
    }
    return data;
  });
};

// Column resolvers - invalidate the parent database to trigger refetch

export const createCompanyDatabaseColumnResolver: UpdateResolver<
  CreateCompanyDatabaseColumnMutation,
  CreateCompanyDatabaseColumnMutationVariables
> = (result, _args, cache) => {
  const newColumn = result.createCompanyDatabaseColumn?.companyDatabaseColumn;
  if (!newColumn) return;

  // Invalidate the CompanyDatabasis entity to trigger refetch of columns
  cache.invalidate({
    __typename: 'CompanyDatabasis',
    id: newColumn.databaseId,
  });
};

export const updateCompanyDatabaseColumnResolver: UpdateResolver<
  UpdateCompanyDatabaseColumnMutation,
  UpdateCompanyDatabaseColumnMutationVariables
> = (result, _args, cache) => {
  const updatedColumn =
    result.updateCompanyDatabaseColumn?.companyDatabaseColumn;
  if (!updatedColumn) return;

  // Invalidate the CompanyDatabasis entity to trigger refetch
  cache.invalidate({
    __typename: 'CompanyDatabasis',
    id: updatedColumn.databaseId,
  });
};

export const deleteCompanyDatabaseColumnResolver: UpdateResolver<
  DeleteCompanyDatabaseColumnMutation,
  DeleteCompanyDatabaseColumnMutationVariables
> = (result, _args, cache) => {
  const deletedColumn =
    result.deleteCompanyDatabaseColumn?.companyDatabaseColumn;
  if (!deletedColumn) return;

  // Invalidate all GetCompanyDatabase queries since we don't have databaseId in the result
  cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === 'companyDatabasis')
    .forEach((field) => {
      cache.invalidate('Query', field.fieldKey);
    });
};

// Import resolver - invalidate database and rows after import

export const importCompanyDatabaseDataResolver: UpdateResolver<
  ImportCompanyDatabaseDataMutation,
  ImportCompanyDatabaseDataMutationVariables
> = (result, args, cache) => {
  if (!result.importCompanyDatabaseData?.success) return;

  const databaseId = args.input.databaseId;

  // Invalidate the CompanyDatabasis entity to trigger refetch of columns and metadata
  cache.invalidate({
    __typename: 'CompanyDatabasis',
    id: databaseId,
  });

  // Invalidate all rows queries for this database
  cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === 'companyDatabaseRowsConnection')
    .forEach((field) => {
      cache.invalidate('Query', field.fieldKey);
    });

  // Clear database lookup caches for this database since content has changed
  clearDatabaseLookupCacheForDatabase(databaseId);
};
